import { AnyAction } from 'redux';
import modelExtend from 'dva-model-extend';
import deepmerge from 'deepmerge';
import { t } from '@lingui/macro';

import { PersonProps } from '../components/shared/Person/Person';
import citizenship from './citizenship';
import { CHANGE_NAME, CHILD_ADDRESS } from '../constants/child';
import { Field } from '../declarations/widget-ums';
import { AddressFields } from '../../types/address';
import { CITIZENSHIP } from '../constants/citizenship';
import {
  CHANGED_FIO_FIELD_NAME,
  EMPTY_PATRONYMIC_LABEL,
  IS_EMPTY_PATRONYMIC_FIELD_NAME,
  PATRONYMIC_FIELD_NAME,
  PERSON_FIELD_NAME,
  FIO_FIELDS,
  SPOUSE_FIELD_NAME,
} from '../constants/fieldNames';
import { createField, getFIOPayload } from '../helpers/fieldHelpers';
import { i18n } from '../helpers/i18n';
import { initialPersonState } from '../helpers/initialState';
import { isSpouseRegisteredInMoscow } from '../selectors/spouse';
import { DocumentState } from '../constants/document';
import documentModel from './document';
import { validateField, validators } from '../validators';

export interface PersonFields extends DocumentState {
  citizenship: Field<CITIZENSHIP>;
  address: AddressFields;
  changedPatronimic: Field;
  birthdate: Field;
  sex: Field;
  changedName: Field;
  deathdate: Field;
  changeName: Field<CHANGE_NAME | null>;
  patronymic: Field;
  changeNameDocIssuer: Field;
  surname: Field;
  name: Field;
  isChangedPatronymicAbsent: Field<boolean>;
  isPatronymicAbsent: Field<boolean>;
  changedSurname: Field;
  birthPlace: Field<string>;
}

export const isEmptyPatronymicFieldName = (key) =>
  Object.values(EMPTY_PATRONYMIC_LABEL).includes(key);

const personModel = modelExtend(citizenship, documentModel, {
  namespace: 'person',
  state: initialPersonState,
  reducers: {
    reset(state) {
      return deepmerge(state, initialPersonState);
    },
    onChange(state: PersonProps, { payload }: AnyAction) {
      const [key] = Object.keys(payload);

      if (isEmptyPatronymicFieldName(key)) {
        const field = IS_EMPTY_PATRONYMIC_FIELD_NAME[key];
        state[field] = { ...state[field], value: true };
        state[PATRONYMIC_FIELD_NAME[key]] = createField(
          i18n._(t`Нет отчества`)
        );
        return state;
      }

      if (
        [
          PERSON_FIELD_NAME.patronymic,
          CHANGED_FIO_FIELD_NAME.changedPatronimic,
        ].includes(key)
      ) {
        const field =
          IS_EMPTY_PATRONYMIC_FIELD_NAME[EMPTY_PATRONYMIC_LABEL[key]];
        const isPatronymicEmpty = {
          [field]: { ...state[field], value: false },
        };
        return { ...state, ...isPatronymicEmpty, ...getFIOPayload(payload) };
      }

      if (FIO_FIELDS.includes(key)) {
        return {
          ...state,
          ...getFIOPayload(payload),
        };
      }

      return { ...state, ...payload };
    },
    setElkData(state: PersonFields, { payload }: AnyAction) {
      return Object.assign(state, payload);
    },
    setFromDraft(state: PersonProps, { payload }: AnyAction) {
      return Object.assign(state, payload);
    },
    'isNameChanged/toggle'(state: PersonFields, { payload }: AnyAction) {
      const {
        changeName: { value },
      } = payload;
      if (value === CHANGE_NAME.match) {
        state.changedSurname = createField();
        state.changedName = createField();
        state.changedPatronimic = createField();
        state.changeNameDocIssuer = createField();
        state.isChangedPatronymicAbsent = createField(false);
      }
      return Object.assign(state, payload);
    },
    'set/error'(state: PersonProps, { payload }: AnyAction) {
      return Object.assign(state, payload);
    },
    'set/error/relativeFields'(state: PersonFields, { payload }: AnyAction) {
      const { fields, error } = payload;
      const newState = fields.reduce((next, field) => {
        const newField = Object.assign(state[field], { error });
        return Object.assign(next, newField);
      }, {});
      return Object.assign(state, newState);
    },
    'set/birthdate/error'(state: PersonFields, { payload }) {
      state.birthdate.error = payload;

      return state;
    },
  },
  effects: {
    *onChange({ type, payload }, { put, select }) {
      const [key] = Object.keys(payload);
      if (
        key === SPOUSE_FIELD_NAME.addressMatchWithApplicant &&
        payload.value === CHILD_ADDRESS.matchWithSpouse
      ) {
        const isRegisteredInMoscow = yield select(isSpouseRegisteredInMoscow);
        yield put({
          type: `child/address/toggle/isRegisteredInMoscow`,
          payload: isRegisteredInMoscow,
        });
      }
      if (!type.includes('child') && isEmptyPatronymicFieldName(key)) {
        yield put({
          type: `form/saveDraft`,
        });
      }
    },
    *'validate/field'({ type, payload, additionalNamespace }, { select, put }) {
      const { name, value, namespace } = payload;
      if (!value) {
        if (!type.includes('child')) {
          yield put({
            type: `form/saveDraft`,
          });
        }
        return;
      }

      const field = additionalNamespace
        ? `${namespace}/${additionalNamespace}.${name}`
        : `${namespace}/${name}`;

      const state = yield select((state) => state);
      const fieldValue = validateField(field, state);
      const newState = deepmerge(state[namespace], fieldValue);

      yield put({
        type: `${namespace}/set/error`,
        payload: newState,
      });

      const { error } = additionalNamespace
        ? fieldValue[additionalNamespace][name]
        : fieldValue[name];
      if (error) {
        yield put({
          type: `form/find/relativeFields/forSetError`,
          payload: {
            field: [namespace, additionalNamespace, name]
              .filter(Boolean)
              .join('.'),
            error,
          },
        });
      } else {
        if (!namespace.includes('child')) {
          yield put({
            type: `form/saveDraft`,
          });
        }
      }
    },
    *'isNameChanged/toggle'({ type }, { put }) {
      if (!type.includes('child')) {
        yield put({
          type: `form/saveDraft`,
        });
      }
    },
  },

  *'check/birthdate/error'({ type }, { select, put }) {
    const [namespace] = type.split('/');
    const state = yield select((state) => state);
    const { error } = validators[`${namespace}.birthdate`](state);

    yield put({
      type: `${namespace}/set/birthdate/error`,
      payload: error,
    });
  },
});

export default personModel;
